import {
  RequireSession,
  churchUrl,
  entitlementsQuery,
  memberPortalHost,
  useHandoff,
  useMyMemberships,
  useOnStaffDoor,
  useSupabase,
  type Membership,
} from "@how/app-kit";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { ErrorState, LoadingState } from "@how/ui";
import { env } from "../lib/env";
import { DoorLayout } from "./DoorLayout";
import { memberChurchesOf, staffChurchOf } from "./door-landing";
import { StaffOnlyPage } from "./StaffOnlyPage";

/**
 * Old /me/schedule links (spec piece 3; ADR 0070 R10): My schedule lives in the member portal now. A staff login whose
 * church has Services goes there at its staff church, through the hand-off, keeping #privacy. Otherwise (no Services
 * there, the staff church's modules could not be read, or the hand-off was refused) the page lists the person's member
 * churches, each opening My schedule, with a link back to the staff dashboard; with no member churches it says plainly
 * that My schedule isn't available here. A login with no staff church sees its member churches the same way, with no
 * staff link. Nothing here is a dead end.
 */
export function MyScheduleMoved() {
  return (
    <RequireSession>
      <MovedToPortal />
    </RequireSession>
  );
}

function MovedToPortal() {
  const memberships = useMyMemberships();
  const handoff = useHandoff();
  const supabase = useSupabase();
  const onDoor = useOnStaffDoor();
  const { hash } = useLocation();
  const staff = memberships.data === undefined ? undefined : staffChurchOf(memberships.data);
  // The staff church's modules (Minor 2): handing off to a portal page that will refuse helps nobody.
  const modules = useQuery({
    ...entitlementsQuery(supabase, staff?.tenantId ?? ""),
    enabled: staff !== undefined,
  });
  const hasServices = modules.data?.has("services") === true;
  const slug = staff !== undefined && hasServices ? staff.slug : null;
  const started = useRef(false);
  useEffect(() => {
    if (slug === null || started.current) return;
    started.current = true;
    handoff.open(
      memberPortalHost(slug, env),
      `/my-schedule${hash === "#privacy" ? "#privacy" : ""}`,
    );
  }, [slug, hash, handoff]);
  if (memberships.isError) {
    return (
      <DoorLayout>
        <ErrorState
          message="Could not load your churches."
          onRetry={() => {
            void memberships.refetch();
          }}
        />
      </DoorLayout>
    );
  }
  if (memberships.data === undefined) return <LoadingState label="Opening My schedule" />;
  const churches = memberChurchesOf(memberships.data);
  if (staff === undefined) {
    return (
      <ElsewherePage
        staffChurchHref={null}
        reason={
          churches.length === 0
            ? "My schedule lives in a church's member portal, and none is open to you."
            : "My schedule is in your church's member portal now."
        }
        churches={churches}
      />
    );
  }
  const staffChurchHref = churchUrl(staff.slug, "staff_dashboard", env, onDoor);
  if (modules.isPending) return <LoadingState label="Opening My schedule" />;
  if (modules.isError || handoff.error !== null) {
    // Neutral on purpose: a refusal and a network failure look the same from here.
    return (
      <ElsewherePage
        staffChurchHref={staffChurchHref}
        reason={`My schedule could not be opened at ${staff.name}.`}
        churches={churches}
      />
    );
  }
  if (!hasServices) {
    return (
      <ElsewherePage
        staffChurchHref={staffChurchHref}
        reason={`${staff.name} doesn't use Services in HoW, so there is no schedule to open there.`}
        churches={churches}
      />
    );
  }
  return <LoadingState label={`Opening My schedule at ${staff.name}`} />;
}

/** The page for an old My schedule link that does not hand off to the staff church (S5). */
function ElsewherePage({
  staffChurchHref,
  reason,
  churches,
}: {
  readonly staffChurchHref: string | null;
  readonly reason: string;
  readonly churches: readonly Membership[];
}) {
  const none = churches.length === 0;
  return (
    <DoorLayout staffChurchHref={staffChurchHref}>
      <StaffOnlyPage
        title={
          none ? "My schedule isn't available to you here" : "My schedule is in the member portal"
        }
        description={
          none
            ? `${reason}${staffChurchHref === null ? "" : " Choose Open the staff dashboard to go back."}`
            : `${reason} Open My schedule from the member portal of a church where you serve.`
        }
        churches={churches}
        path="/my-schedule"
      />
    </DoorLayout>
  );
}
